// İSG Portal — Kullanıcılar (Users — tenant-scoped) API çağrıları + tipleri.
//
// KAYNAK: api-contract.md §12 (Kullanıcılar — tenant-scoped). Backend kanonu; burada
// **varsayım YOK** — endpoint yolu, istek/yanıt alanları ve kimlik kuralları (Result
// unwrap `value`; liste → `value` + `total`/`filteredTotal`) doğrudan sözleşmeden
// türetilmiştir. Serileştirme camelCase'dir (§1).
//
// **Tenant izolasyonu (§4b/§12).** Uç çağıranın firmasına (token `CompanyId`) her zaman
// kapanır; istemci firma kimliği GÖNDERMEZ — sunucu token'dan çözer. Başka firmanın
// kullanıcısı hiçbir koşulda dönmez. Kullanıcılar merkezî identity store'unda yaşar
// (tenant-shard'da değil); izolasyon Visitors host çözümüyle aynı düz `CompanyId`
// filtresiyle sağlanır (§12 uygulama notu).
//
// **Kapsam (tam CRUD + atanabilir rol seçici).** Doğuş nedeni: Ziyaretçi check-in'in
// ev sahibi (host) seçici altyapısı — `GET /users`. FE selectlist map'i kanoniktir:
// `{ value: uuid, label: "firstName lastName" }` ([[feedback_bibulut_selectlist_canonical_shape]]).
// Liste aktif + pasif döner (`isActive` bayrağı); host seçici FE tarafında
// `isActive === true` ile süzer (§12 aktiflik kararı). Expert paneli kullanıcı
// ekle/düzenle/pasifleştir ekranları aynı liste + `get`/`create`/`update`/`delete` +
// rol seçici (`GET /roles`, §12.2) uçlarını kullanır. Parola **yalnız istekte** taşınır
// (hiçbir yanıtta dönmez; loglanmaz — §12).

import { api } from './client';
import type { Empty } from './envelope';

/**
 * Firma kullanıcısı — api-contract §12 (`UserDto`). `roleName` = kullanıcının ilk aktif
 * rolünün adı (ada göre ilki; kararlı), rol atanmamışsa `null`. Sıralı iç `Id`
 * istemciye sızmaz (yalnız `uuid`).
 */
export interface UserDto {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Aktiflik bayrağı — host seçici FE'de `isActive === true` ile süzer (§12). */
  isActive: boolean;
  roleName?: string | null;
}

/**
 * Kullanıcı liste parametreleri — api-contract §12.1 (`GET /users`) + §3 paging. Filtre
 * yüzeyi bilinçli olarak dar: `search` (ad/soyad/e-posta case-insensitive substring,
 * OR-bağlı) + `page`/`pageSize`. Sunucu-tarafı `isActive` filtresi bu turda YOK; sıra
 * ad, soyad (backend'de kararlı — istemci sort göndermez).
 */
export interface UserListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

/** Boş/whitespace dizeyi `undefined`'a indirger (searchParams'ta atlanır). */
function nonEmpty(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

/**
 * `GET /users` (`list`) — firmanın kullanıcıları (sayfalı + aranabilir). Aktif + pasif
 * döner; host seçici FE'de `isActive === true` ile süzer. → `value` + `total`/
 * `filteredTotal` (§3).
 */
export function listUsers(params: UserListParams = {}) {
  return api.list<UserDto>('users', {
    searchParams: {
      search: nonEmpty(params.search),
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/**
 * Kullanıcı detayı — api-contract §12 (`UserDetailDto`). Liste özetinden ayrılır: tek
 * `roleName` yerine **tam rol seti** iki hizalı dizide (`roleUuids` düzenle formunu
 * ön-seçer, `roleNames` gösterir; ada göre ordinal aynı sıra). Parola hiçbir yanıtta
 * yer almaz; sıralı iç `Id` sızmaz.
 */
export interface UserDetailDto {
  uuid: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  roleUuids: string[];
  roleNames: string[];
}

/**
 * Atanabilir rol seçeneği — api-contract §12.2 (`RoleOptionDto`; `GET /roles`). Yalnız
 * atanabilir roller (`IsActive` + `IsSystemAdmin=false` + global VEYA çağıranın firması).
 * Sistem-admin rolü + başka firmanın rolü **hiçbir koşulda dönmez** (backend süzer).
 */
export interface RoleOptionDto {
  uuid: string;
  name: string;
}

/**
 * Kullanıcı oluşturma gövdesi — api-contract §12.1 (`POST /users`). `CompanyId`
 * **token'dan** çözülür (istemci göndermez). Parola politikası (min 8 + harf + rakam; §2)
 * ihlali → `400 PASSWORD_POLICY_ERROR`; e-posta çakışması → `409 USER_ALREADY_EXISTS`;
 * atanamaz rol → `400 ROLE_NOT_ASSIGNABLE`. **Parola plaintext ne saklanır ne loglanır.**
 */
export interface UserCreateRequest {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  roleUuids?: string[];
}

/**
 * Kullanıcı güncelleme gövdesi — api-contract §12.1 (`PUT /users/{uuid}`). Rol seti
 * `roleUuids` **gönderildiğinde replace** edilir; `null`/atlanırsa değişmez. Guard'lar:
 * kendini pasifleştirme → `409 CANNOT_DEACTIVATE_SELF`; atanamaz rol → `400
 * ROLE_NOT_ASSIGNABLE`; yok/başka firma → `404 USER_NOT_FOUND`.
 */
export interface UserUpdateRequest {
  firstName: string;
  lastName: string;
  isActive: boolean;
  roleUuids?: string[] | null;
}

/**
 * `GET /users/{uuid}` (`get`) — tek kullanıcı (tam rol seti ile). Firma scope +
 * soft-delete: başka firma / silinmiş → `404 USER_NOT_FOUND`. → `UserDetailDto`.
 */
export function getUser(uuid: string) {
  return api.get<UserDetailDto>(`users/${uuid}`);
}

/**
 * `POST /users` (`create`) — firmaya yeni kullanıcı; yeni kimlik `value.uuid` (201).
 * Doğrulama sırası (§12.1): zorunlu alan → parola politikası → e-posta çakışması → rol
 * atanabilirlik. Parola **yalnız istekte** taşınır.
 */
export function createUser(body: UserCreateRequest) {
  return api.post<UserDetailDto>('users', { json: body });
}

/**
 * `PUT /users/{uuid}` (`update`) — ad/aktiflik + rol seti (replace). Kendini
 * pasifleştirme guard'ı sunucudadır (`409 CANNOT_DEACTIVATE_SELF`) — FE de UI'da engeller.
 */
export function updateUser(uuid: string, body: UserUpdateRequest) {
  return api.put<UserDetailDto>(`users/${uuid}`, { json: body });
}

/**
 * `DELETE /users/{uuid}` (`delete`) — **soft-sil** (`IsDeleted=true` + `IsActive=false`).
 * Kendini silme → `409 CANNOT_DELETE_SELF`; yok/başka firma → `404 USER_NOT_FOUND`. →
 * `Result<Empty>` (200 envelope; 204 yok — [[feedback_bibulut_no_bodyless_status_modern_result]]).
 */
export function deleteUser(uuid: string) {
  return api.delete<Empty>(`users/${uuid}`);
}

/**
 * `GET /roles` (`list`, §12.2) — kullanıcı ekle/düzenle formundaki **rol seçicinin**
 * kaynağı. Yalnız **atanabilir** roller döner (ada göre sıralı). Sayfasız düz liste →
 * `RoleOptionDto[]` (envelope `value`).
 */
export function listAssignableRoles() {
  return api.get<RoleOptionDto[]>('roles');
}
